// =====================================================================
// src/components/intl-acca/PassPurchaseCard.tsx
// =====================================================================
// Tournament Pass surface (PRD §6.5 + §4 Stream 1).
//
// Critical: this card does NOT charge. It surfaces the price + CTA;
// the host wires onPurchase to a Paystack checkout that calls
// public.intl_acca_record_pass(...) ONLY on the verified callback.
// `existingPass` is the row from public.intl_acca_pass_purchases for
// the current user — when present, the card renders the "already held"
// state with countdown to expires_at (DB-forced; no extensions).

import type { IntlAccaTournament, IntlAccaPassPurchase } from "../../db/intl-acca-types";

interface PassPurchaseCardProps {
  tournament: IntlAccaTournament;
  /** The current user's existing pass for this tournament, if any. */
  existingPass?: IntlAccaPassPurchase | null;
  /** Set to true when the viewer's subscription tier auto-includes the pass. */
  complimentaryViaSubscription?: boolean;
  /** Wired to the host's Paystack checkout. */
  onPurchase?: () => void;
  /** Optional accent-styling override — `true` highlights this as primary. */
  primary?: boolean;
  /** Inclusions list — host curates per tournament; defaults are PRD §3.2 / §6.5. */
  inclusions?: string[];
}

const DEFAULT_INCLUSIONS = [
  "Focus + Value Acca · every matchday",
  "Reasoning note on every leg (PRD §12)",
  "CLV audit · settled vs Pinnacle close",
  "Slip Share card + referral link",
  "Pass auto-expires at tournament end · no rollover",
];

function daysUntil(iso: string): number {
  const now = Date.now();
  const target = new Date(iso).getTime();
  return Math.max(0, Math.ceil((target - now) / 86_400_000));
}

export function PassPurchaseCard({
  tournament,
  existingPass,
  complimentaryViaSubscription,
  onPurchase,
  primary,
  inclusions = DEFAULT_INCLUSIONS,
}: PassPurchaseCardProps): JSX.Element {
  const t = tournament;
  const priceLabel =
    t.pass_price_usd === null ? "—" : `$${t.pass_price_usd}`;
  const onSale = t.pass_active && t.pass_price_usd !== null;

  // Entitled via subscription tier (Sika Pro+ / founder) and no
  // purchase row: clean "included" state — NO price, NO "TOURNAMENT
  // PASS" sales tag, NO purchase CTA. This is not a paywall.
  if (complimentaryViaSubscription && !existingPass) {
    return (
      <div className="ia-pass-card ia-pass-card-primary">
        <div className="ia-eyebrow" style={{ color: "var(--pos)" }}>
          ★ Pass included
        </div>
        <h3
          className="ia-title"
          style={{ fontSize: "var(--sz-head-md)", marginTop: 12 }}
        >
          {t.name}. <em>Included with your subscription.</em>
        </h3>
        <p className="ia-sub" style={{ marginTop: 14, fontSize: "var(--sz-body-sm)" }}>
          Your tier auto-includes the Tournament Pass — full Focus +
          Value Acca, every reasoning note, and the post-tournament CLV
          audit. Nothing to purchase.
        </p>
      </div>
    );
  }

  // Already held — comp or paid
  if (existingPass) {
    const remaining = daysUntil(existingPass.expires_at);
    return (
      <div className="ia-pass-card ia-pass-card-primary">
        <div className="ia-eyebrow" style={{ color: "var(--pos)" }}>
          ★ Pass active
        </div>
        <h3
          className="ia-title"
          style={{ fontSize: "var(--sz-head-md)", marginTop: 12 }}
        >
          {t.name}.
          {existingPass.is_complimentary ? (
            <em> Complimentary via Sika Pro.</em>
          ) : null}
        </h3>
        <div
          className="ia-mono"
          style={{
            marginTop: 18,
            fontSize: "var(--sz-mono-md)",
            color: "var(--text-secondary)",
          }}
        >
          Expires {new Date(existingPass.expires_at).toLocaleDateString("en-GB", {
            day: "numeric",
            month: "short",
            year: "numeric",
          })}{" "}
          · {remaining}d remaining
        </div>
        <p className="ia-sub" style={{ marginTop: 14, fontSize: "var(--sz-body-sm)" }}>
          Pass auto-expires at tournament end — no extensions (PRD §12).
        </p>
      </div>
    );
  }

  return (
    <div className={primary ? "ia-pass-card ia-pass-card-primary" : "ia-pass-card"}>
      {primary ? (
        <span
          style={{
            position: "absolute",
            top: -14,
            left: 24,
            background: "var(--accent)",
            color: "var(--sz-text-inverse)",
            padding: "6px 12px",
            fontFamily: "var(--sz-font-mono)",
            fontSize: 10,
            letterSpacing: "0.22em",
            fontWeight: 700,
            borderRadius: "var(--sz-radius-sm)",
          }}
        >
          ★ TOURNAMENT PASS
        </span>
      ) : null}

      <div className="ia-eyebrow">{t.name} · {t.region}</div>
      <h3
        className="ia-title"
        style={{ fontSize: "var(--sz-head-md)", marginTop: 12, lineHeight: 1.05 }}
      >
        Pass.
      </h3>

      {/* Price */}
      <div style={{ marginTop: 18 }}>
        <span className="ia-pass-price">{priceLabel}</span>
        <span className="ia-pass-price-cents">USD · GHS / NGN via Paystack</span>
      </div>

      {/* Honest-empty pricing path */}
      {t.pass_price_usd === null ? (
        <p
          className="ia-sub"
          style={{ fontSize: "var(--sz-body-sm)", marginTop: 14, color: "var(--text-tertiary)" }}
        >
          Price not yet set. The pass is not on sale until the founder activates it
          via the admin RPC.
        </p>
      ) : null}

      {complimentaryViaSubscription ? (
        <div
          className="ia-pill ia-pill-pos"
          style={{ marginTop: 14, fontSize: "var(--sz-mono-sm)" }}
        >
          ★ Included with your subscription
        </div>
      ) : null}

      {/* Inclusions */}
      <div style={{ marginTop: 18, display: "flex", flexDirection: "column", gap: 8 }}>
        {inclusions.map((it, i) => (
          <div
            key={i}
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 10,
              fontSize: "var(--sz-body-sm)",
              color: "var(--text-secondary)",
              lineHeight: 1.45,
            }}
          >
            <span
              aria-hidden
              style={{
                color: "var(--accent)",
                fontFamily: "var(--sz-font-mono)",
                fontSize: 11,
                lineHeight: 1.45,
              }}
            >
              ✓
            </span>
            <span>{it}</span>
          </div>
        ))}
      </div>

      {/* CTA */}
      {onSale && onPurchase && !complimentaryViaSubscription ? (
        <button
          type="button"
          onClick={onPurchase}
          style={{
            marginTop: 24,
            width: "100%",
            background: "var(--accent)",
            color: "var(--sz-text-inverse)",
            border: "none",
            borderRadius: "var(--sz-radius-md)",
            padding: "14px 18px",
            fontFamily: "var(--sz-font-mono)",
            fontSize: 11,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            fontWeight: 700,
            cursor: "pointer",
          }}
        >
          Unlock {t.name} Pass →
        </button>
      ) : !onSale ? (
        <div
          style={{
            marginTop: 24,
            padding: "12px 14px",
            background: "var(--bg-inset)",
            border: "1px solid var(--border)",
            borderRadius: "var(--sz-radius-md)",
            textAlign: "center",
          }}
        >
          <span
            className="ia-mono"
            style={{
              fontSize: "var(--sz-mono-xs)",
              letterSpacing: "0.22em",
              color: "var(--text-tertiary)",
              textTransform: "uppercase",
            }}
          >
            Not yet on sale
          </span>
        </div>
      ) : null}
    </div>
  );
}
